/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";

/** @module browser/classes/overscroll-blocker */
import { Types } from '@codistica/types';
import { canScrollX } from '../modules/element-utils/internals/can-scroll-x.js';
import { canScrollY } from '../modules/element-utils/internals/can-scroll-y.js';
import { whichScrollLimitReached } from '../modules/element-utils/internals/which-scroll-limit-reached.js'; // TODO: FIX ISSUES IN FIREFOX (STICKY ON LIMITS).
// TODO: TRY FIXING ISSUE WHEN TRYING TO SCROLL DIAGONALLY AGAINST A LIMIT. NON LIMIT AXIS GETS BLOCKED. (SET SCROLL MANUALLY? WHEN DEFAULT PREVENTED. WITH OPTIONS).
// TODO: USE REACT SPRING TO REVIVE AXIS IF NEEDED (THIS COULD ONLY BE DONE IN @codistica/react).

var overscrollBlockerSchema = new Types({
  options: {
    type: 'Object',
    def: {
      blockX: {
        type: 'boolean',
        def: true
      },
      blockY: {
        type: 'boolean',
        def: true
      },
      contentAware: {
        type: 'boolean',
        def: false
      }
    }
  }
});
/**
 * @typedef overscrollBlockerOptionsType
 * @property {boolean} [blockX=true] - Block overscroll on X axis.
 * @property {boolean} [blockY=true] - Block overscroll on Y axis.
 * @property {boolean} [contentAware=false] - Traverse DOM searching for other scrollable elements to take into consideration.
 */

/**
 * @classdesc Utility class to handle overscroll effect.
 */

var OverscrollBlocker = /*#__PURE__*/function () {
  /**
   * @description Constructor.
   * @param {overscrollBlockerOptionsType} [options] - Overscroll handling options.
   */
  function OverscrollBlocker(options) {
    var _context, _context2;

    _classCallCheck(this, OverscrollBlocker);

    var _overscrollBlockerSch = overscrollBlockerSchema.validate({
      options: options
    });

    options = _overscrollBlockerSch.options;
    this.touchPos = {
      x: 0,
      y: 0
    };
    this.isMultiTouch = false;
    /** @type {overscrollBlockerOptionsType} */

    this.options = options; // BIND METHODS

    this.handler = _bindInstanceProperty(_context = this.handler).call(_context, this);
    this.shouldBlock = _bindInstanceProperty(_context2 = this.shouldBlock).call(_context2, this);
  }
  /**
   * @instance
   * @description Handler.
   * @param {Object<string,*>} e - Triggering event.
   * @returns {void} Void.
   */


  _createClass(OverscrollBlocker, [{
    key: "handler",
    value: function handler(e) {
      if (!e.cancelable || this.isMultiTouch) {
        return;
      }

      var delta = {
        x: null,
        y: null
      }; // NORMALIZE EVENT

      switch (e.type) {
        case 'touchstart':
          this.touchPos.x = e.touches[0].screenX;
          this.touchPos.y = e.touches[0].screenY;
          this.isMultiTouch = e.touches.length > 1;
          return;

        case 'touchmove':
          delta.x = this.touchPos.x - e.touches[0].screenX;
          delta.y = this.touchPos.y - e.touches[0].screenY;
          this.touchPos.x = e.touches[0].screenX;
          this.touchPos.y = e.touches[0].screenY;
          break;

        case 'touchend':
          this.touchPos.x = 0;
          this.touchPos.y = 0;
          this.isMultiTouch = e.touches.length > 1;
          return;

        case 'wheel':
          delta.x = e.deltaX;
          delta.y = e.deltaY;
          break;

        default:
          // NO VALID EVENT
          return;
      }

      if (this.options.contentAware) {
        var currentElem = e.target;
        var flag = true;

        do {
          if (!this.shouldBlock(currentElem, delta.x, delta.y)) {
            return;
          }

          if (currentElem === e.currentTarget) {
            flag = false;
          } else {
            currentElem = currentElem.parentElement;
          }
        } while (currentElem && flag);

        e.preventDefault();
      } else {
        if (this.shouldBlock(e.currentTarget, delta.x, delta.y)) {
          e.preventDefault();
        }
      }
    }
    /**
     * @instance
     * @description Determines if current action should be blocked for passed element.
     * @param {HTMLElement} elem - Element.
     * @param {number} deltaX - Action deltaX.
     * @param {number} deltaY - Action deltaY.
     * @returns {boolean} Result.
     */

  }, {
    key: "shouldBlock",
    value: function shouldBlock(elem, deltaX, deltaY) {
      var limitReached = whichScrollLimitReached(elem, deltaX, deltaY);

      if (this.options.blockX && deltaX && (!canScrollX(elem) || limitReached === 'left' || limitReached === 'right')) {
        return true;
      } else if (this.options.blockY && deltaY && (!canScrollY(elem) || limitReached === 'top' || limitReached === 'bottom')) {
        return true;
      }

      return false;
    }
  }]);

  return OverscrollBlocker;
}();

export { OverscrollBlocker };